import React from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { contact } from '../data/mock';

export default function PrivacyPolicyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="How Salihi Consulting Services collects, uses, and safeguards personal information of visitors, clients and partners."
        path="/privacy-policy"
      />
      <PageHero
        breadcrumb="Privacy Policy"
        eyebrow="Legal"
        title="Privacy"
        italicTitle="Policy."
        subtitle="Your privacy matters. This policy explains what information Salihi Consulting Services collects, why we collect it, and how we keep it safe."
      />

      <section className="py-16 sm:py-20 bg-[#0d2143]">
        <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-10">
          <article>
            <Block title="1. Introduction">
              Salihi Consulting Services (“we”, “us”, “our”) is committed to protecting
              the privacy of every visitor, client, and partner that interacts with us
              — whether through this website, by email, in person, or during a
              professional engagement. This Privacy Policy describes the personal
              information we collect, the purposes for which we use it, and the
              safeguards we apply.
            </Block>
            <Block title="2. Information we collect">
              <List items={[
                { label: 'Information you provide directly', desc: 'Such as your name, email, organization, phone number, and the contents of any inquiry, form, or document you share with us.' },
                { label: 'Information collected automatically', desc: 'Including basic technical data such as IP address, browser type, device, referring page, and aggregated usage statistics.' },
                { label: 'Information from engagements', desc: 'During professional engagements, we may collect business information necessary to deliver the services you have requested, always subject to a clear engagement scope.' },
              ]} />
            </Block>
            <Block title="3. How we use your information">
              We use personal information to respond to inquiries, deliver contracted
              services, manage client relationships, comply with legal obligations,
              improve our website, and — where you have opted in — to share relevant
              updates about our work. We never sell your personal information.
            </Block>
            <Block title="4. Legal basis for processing">
              We process personal information on the basis of your consent, the
              performance of a contract with you, compliance with legal obligations,
              and our legitimate interests in operating a professional services firm
              — balanced against your rights and expectations.
            </Block>
            <Block title="5. Sharing of information">
              We share personal information only when necessary: with members of our
              team who need it to deliver the engagement; with carefully selected
              service providers under written confidentiality and data-protection
              obligations; and where required to comply with law, regulation or a
              lawful request from authorities.
            </Block>
            <Block title="6. Data security">
              We apply organizational, physical, and technical safeguards designed to
              protect personal information from unauthorized access, alteration, loss
              or disclosure. Access to client data is restricted on a need-to-know
              basis, and our team is bound by confidentiality obligations.
            </Block>
            <Block title="7. Data retention">
              We retain personal information only for as long as it is necessary for
              the purposes for which it was collected, including to satisfy any legal,
              accounting, or reporting obligations. Once retention is no longer
              required, information is securely deleted or anonymized.
            </Block>
            <Block title="8. Your rights">
              Subject to applicable law, you may have the right to access, correct,
              update, restrict, or request deletion of your personal information, as
              well as the right to withdraw any consent you previously gave. To exercise
              these rights, please contact us using the details below.
            </Block>
            <Block title="9. International transfers">
              Where personal information is transferred outside of its country of
              origin, we ensure that adequate safeguards are in place to protect that
              information consistent with this policy.
            </Block>
            <Block title="10. Updates">
              We may update this Privacy Policy from time to time. Material changes
              will be posted on this page with a revised effective date.
            </Block>
            <Block title="11. Contact us">
              For privacy questions or to exercise your rights, please contact us at{' '}
              <a href={`mailto:${contact.email}`} className="text-[#e8b863] underline underline-offset-2">{contact.email}</a>{' '}
              or by phone at {contact.phone}.
            </Block>
          </article>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-white font-medium text-[22px] sm:text-[26px] leading-tight tracking-tight mb-4">
        {title}
      </h2>
      <div className="text-white/78 text-[15px] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.label} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-5">
          <p className="text-[#e8b863] font-semibold text-[14.5px] mb-1.5">{it.label}</p>
          <p className="text-white/75 text-[14px] leading-relaxed">{it.desc}</p>
        </li>
      ))}
    </ul>
  );
}
